/**
 * SortableHandle - Drag handle for sortable list items.
 *
 * Provides a consistent drag grip button with proper cursor states,
 * accessibility attributes, and integration with dnd-kit.
 */

import { GripVertical } from 'lucide-react';
import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { cn } from '@lean-agility/design-system/react';

export interface SortableHandleProps {
  /** Whether dragging is disabled */
  disabled?: boolean;
  /** dnd-kit listener map from useSortable */
  listeners?: SyntheticListenerMap;
  /** dnd-kit attributes from useSortable */
  attributes?: DraggableAttributes;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SortableHandle - Consistent drag handle for sortable items.
 *
 * @example
 * ```tsx
 * const { attributes, listeners } = useSortable({ id });
 *
 * <SortableHandle
 *   disabled={isDraft}
 *   listeners={listeners}
 *   attributes={attributes}
 * />
 * ```
 */
export function SortableHandle({ disabled = false, listeners, attributes, className }: SortableHandleProps) {
  return (
    <button
      type="button"
      className={cn(
        'touch-none transition-colors',
        disabled
          ? `
            cursor-not-allowed text-text-disabled opacity-50
            dark:text-text-disabled
          `
          : `
            cursor-grab text-text-subtle
            hover:text-text-secondary
            active:cursor-grabbing
            dark:text-text-muted
            dark:hover:text-text-secondary
          `,
        className,
      )}
      {...attributes}
      {...(disabled ? {} : listeners)}
    >
      <GripVertical className="size-4" />
    </button>
  );
}
